import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import InsertionEditor, { ListingParams } from '../InsertionEditor'
import { Flow } from '../../../../@types/flow-types'
import { ObjectParams } from '../../../../@types/types'
import Loading from '../../../common/display/loading/Loading'
import { InitState } from '../../../../redux/reducers/root/initialState'
import { DisplayObjectChooserType } from '../../../../@types/object-chooser-types'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  getObjectThunk,
  hideObjectChooser,
  objectChooserWasShown,
  updateFlow,
} from '../../../../redux/reducers/flow/flowFeature'
import ObjectChooserPage from '../objectChooser/ObjectChooserPage'
import { PageHeader } from './PageHeader'
import { trackingOnPageLoad } from '../../../../tracking/tracking'
import { useExperimentTracking } from '../UseExperimentTracking'

interface InsertionEditorWrapperProps {
  flow: Flow
}

const getObjectQueryParams = (searchParams: URLSearchParams, flow: Flow): ObjectParams | undefined => {
  const buildingId = searchParams.get('buildingId') as string
  const unitId = searchParams.get('unitId') as string
  const entryMode = searchParams.get('entryMode') as string

  return buildingId
    ? {
        type: flow.flowInformation.metaData.category,
        transaction: flow.flowInformation.metaData.transaction,
        segment: flow.flowInformation.metaData.segmentation,
        buildingId: buildingId,
        ...(unitId && { unitId: unitId }),
        ...(entryMode && { entryMode: entryMode }),
      }
    : undefined
}

const InsertionEditorWrapper = ({ flow }: InsertionEditorWrapperProps) => {
  const { id } = useParams<ListingParams>()
  const searchParams = new URLSearchParams(window.location.search)
  useExperimentTracking({
    flowMetadata: flow.flowInformation.metaData,
    hasHeyImmo: flow.flowInformation.options?.hasHeyImmo === true,
    recommendedPriceEnabled: flow.flowInformation.options?.recommendedPriceEnabled === true,
  })
  const displayObjectChooser = useAppSelector(
    (state: InitState) => state.flowFeature.objectChooserInformation.displayObjectChooser
  )
  const dispatch = useAppDispatch()
  const objectParams = getObjectQueryParams(searchParams, flow)
  const shouldCreteNewObject = searchParams.get('newObject') === 'true'
  const shouldLoadObjectChooserContext = !id && !objectParams && !shouldCreteNewObject

  useEffect(() => {
    dispatch(updateFlow(flow))
  }, [dispatch, flow])

  useEffect(() => {
    if (!shouldLoadObjectChooserContext) {
      dispatch(hideObjectChooser())
      return
    }

    const { defaultStateValues, flowInformation } = flow
    const { transaction, segmentation, category } = flowInformation.metaData
    dispatch(
      getObjectThunk({
        defaultStateValues: defaultStateValues,
        objectParams: {
          type: category,
          transaction: transaction,
          segment: segmentation,
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Effect for tracking and object chooser shown state
  useEffect(() => {
    if (displayObjectChooser === DisplayObjectChooserType.YES) {
      dispatch(objectChooserWasShown())
      trackingOnPageLoad(flow.flowInformation, undefined, 'object_chooser')
    }
  }, [dispatch, flow, displayObjectChooser, id])

  const element = window.document.getElementById('page-content')

  return (
    <>
      {[DisplayObjectChooserType.LOADING, DisplayObjectChooserType.UNKNOWN].includes(displayObjectChooser) && (
        <>
          <PageHeader displayObjectChooser={displayObjectChooser} flowInformation={flow.flowInformation} />
          <Loading />
        </>
      )}
      {[DisplayObjectChooserType.NO].includes(displayObjectChooser) && (
        <>
          <PageHeader displayObjectChooser={displayObjectChooser} flowInformation={flow.flowInformation} />
          <InsertionEditor flow={flow} objectParams={objectParams} />
        </>
      )}
      {[DisplayObjectChooserType.YES].includes(displayObjectChooser) && (
        <div style={{ minHeight: element?.clientHeight, display: 'flex', flexDirection: 'column' }}>
          <PageHeader displayObjectChooser={DisplayObjectChooserType.YES} flowInformation={flow.flowInformation} />
          <ObjectChooserPage flow={flow} />
        </div>
      )}
    </>
  )
}

export default InsertionEditorWrapper
